import { Matrix } from "@babylonjs/core/Maths/math.vector";
import { ListenableEvent, Log } from "../../utils";
import { PropertyChangedResult } from "../prop";
import { getLocalMatrix, removeFromScene, setLocalMatrix, updateFrozenWorldMatrix } from "../utils/MeshUtils";
import { BoundingBox } from "./BoundingBox";
import { NodeUserData } from "./NodeUserData";
export var ElementChangedFlags = {
    None: 0x00000000,
    Added: 0x00000001,
    Removed: 0x00000002,
    BoundingBox: 0x00000004,
    Transformation: 0x00000008,
    Properties: 0x00000010,
    PropertyValues: 0x00000020
};
export var ElementCategory = {
    None: 0,
    Interior: 1,
    FloorPlan: 2,
    Wall: 3,
    WallOpening: 4,
    WallAddition: 5
};
export var UndoOperation = {
    Delete: 0,
    Transform: 1
};
var SceneElement = /** @class */ (function () {
    function SceneElement(pModel, pNode) {
        this.eventAdded = new ListenableEvent(this);
        this.eventRemoved = new ListenableEvent(this);
        this.eventBoundingBoxChanged = new ListenableEvent(this);
        this.eventTransformationChanged = new ListenableEvent(this);
        this.eventPropertiesChanged = new ListenableEvent(this);
        this.mModel = pModel;
        this.mBoundingBox = null;
        this.mBoundingBoxVisible = null;
        this.mTransform = null;
        this.mTransformInverted = Matrix.Identity();
        this.mWorldTransform = null;
        this.mWorldTransformInverted = Matrix.Identity();
        this.mIsSelected = false;
        this.mIsInScene = true;
        this.mChangedFlags = ElementChangedFlags.None;
        this.mChangeDepth = 0;
        this.mChangeNotifyParent = false;
        this.setNode(pNode);
    }
    Object.defineProperty(SceneElement.prototype, "node", {
        get: function () {
            return (this.mNode);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SceneElement.prototype, "isSelected", {
        get: function () {
            return (this.mIsSelected);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SceneElement.prototype, "isDisposed", {
        get: function () {
            return (this.mNode === undefined); //see dispose()
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SceneElement.prototype, "transform", {
        get: function () {
            this.updateCachedTransforms();
            return (this.mTransform);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SceneElement.prototype, "transformInverted", {
        get: function () {
            this.updateCachedTransforms();
            return (this.mTransformInverted);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SceneElement.prototype, "worldTransform", {
        get: function () {
            this.updateCachedTransforms();
            return (this.mWorldTransform);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SceneElement.prototype, "worldTransformInverted", {
        get: function () {
            this.updateCachedTransforms();
            return (this.mWorldTransformInverted);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SceneElement.prototype, "boundingBox", {
        /**
         * Complete bounding box of this element inluding invisible nodes.
         */
        get: function () {
            if (this.mBoundingBox == null) {
                this.mBoundingBox = this.mNode == null ? new BoundingBox() :
                    BoundingBox.FromNode(this.mNode);
            }
            return (this.mBoundingBox);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SceneElement.prototype, "boundingBoxVisible", {
        /**
         * Bounding box of this element inluding only visible elements.
         */
        get: function () {
            if (this.mBoundingBoxVisible == null) {
                this.mBoundingBoxVisible = this.mNode == null ? new BoundingBox() :
                    BoundingBox.FromNode(this.mNode, true);
            }
            return (this.mBoundingBoxVisible);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SceneElement.prototype, "category", {
        get: function () {
            return (ElementCategory.None);
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Should be called if the element was removed from the scene and will never be used again.
     */
    SceneElement.prototype.dispose = function () {
        if (this.mNode != null) {
            this.mNode.dispose();
            NodeUserData.SetData(this.mNode, SceneElement.NODEDATA_SCENEELEMENT, null);
        }
        this.mNode = undefined;
        this.mBoundingBox = null;
        this.mBoundingBoxVisible = null;
        this.mIsInScene = false;
    };
    SceneElement.prototype.canSelect = function () {
        return (this.mNode != null && this.isInScene());
    };
    SceneElement.prototype.canSelectSubElement = function () {
        return (false);
    };
    SceneElement.prototype.canTranslate = function () {
        return (true);
    };
    SceneElement.prototype.canRotate = function () {
        return (true);
    };
    SceneElement.prototype.canDelete = function () {
        return (true);
    };
    SceneElement.prototype.setTransform = function (pTransform) {
        if (this.mNode == null)
            return (false);
        var tChanged = setLocalMatrix(this.mNode, pTransform);
        if (tChanged)
            this.updateTransformation();
        return (tChanged);
    };
    /**
     * Should be called after the transformation of this.node was modified directly instead
     * of using this.setTransform(). This function updates frozen world matrices and
     * calls notifyChange with ElementChangedFlags.Transformation.
     */
    SceneElement.prototype.updateTransformation = function (pUpdateChildNodes) {
        if (pUpdateChildNodes === void 0) { pUpdateChildNodes = true; }
        if (this.mNode != null) {
            updateFrozenWorldMatrix(this.mNode, pUpdateChildNodes);
            this.notifyChange(ElementChangedFlags.Transformation);
        }
    };
    SceneElement.prototype.getMainElement = function () {
        return (this);
    };
    SceneElement.prototype.getParentElement = function () {
        if (this.mNode != null && this.mNode.parent != null)
            return (SceneElement.FromNode(this.mNode.parent, true));
        return (null);
    };
    SceneElement.prototype.getTopLevelElement = function () {
        var tParent = this.getParentElement();
        if (tParent != null)
            return (tParent.getTopLevelElement());
        else
            return (this);
    };
    /**
     * Returns this element if it is currently selectable otherwise the next parent
     * which is selectable.
     */
    SceneElement.prototype.getSelectableElement = function () {
        if (this.canSelect())
            return (this);
        return (this.getSelectableParentElement());
    };
    /**
     * Returns the next selectable parent or this if there is no parent.
     */
    SceneElement.prototype.getSelectableParentElement = function () {
        var tParent = this.getParentElement();
        if (tParent != null)
            return (tParent.getSelectableElement());
        else
            return (this);
    };
    /**
     * Can be overridden to define a custom bounding box for the selection highlight.
     * Defaults to SceneElement.boundingBoxVisible (does not return a clone).
     */
    SceneElement.prototype.getSelectionHighlightBoundingBox = function () {
        return (this.boundingBoxVisible);
    };
    /**
     * Can be overridden to disable the default selection highlight for this element. (Default: true)
     */
    SceneElement.prototype.isSelectionHighlightEnabled = function () {
        return (true);
    };
    SceneElement.prototype.notifyChange = function (pFlags, pNotifyParent) {
        if (pNotifyParent === void 0) { pNotifyParent = true; }
        if (pFlags == null || pFlags === ElementChangedFlags.None)
            return;
        this.mChangedFlags |= pFlags;
        this.mChangeNotifyParent = this.mChangeNotifyParent || pNotifyParent;
        this.handleImmediateChanges();
        if (this.mChangeDepth === 0) {
            this.handleDelayedChanges();
            //trigger events
            if (this.wasFlagChanged(ElementChangedFlags.Added))
                this.eventAdded.trigger();
            var tWasRemoved = false;
            if (this.wasFlagChanged(ElementChangedFlags.Removed)) {
                this.eventRemoved.trigger();
                tWasRemoved = true;
            }
            if (this.wasFlagChanged(ElementChangedFlags.BoundingBox))
                this.eventBoundingBoxChanged.trigger();
            if (this.wasFlagChanged(ElementChangedFlags.Transformation))
                this.eventTransformationChanged.trigger();
            if (this.wasFlagChanged(ElementChangedFlags.Properties |
                ElementChangedFlags.PropertyValues)) {
                this.eventPropertiesChanged.trigger(this.wasFlagChanged(ElementChangedFlags.Properties) ?
                    PropertyChangedResult.Properties : PropertyChangedResult.Value);
            }
            //notify parent that its bounding box has changed
            if (pNotifyParent && this.wasFlagChanged(ElementChangedFlags.Added |
                ElementChangedFlags.Removed |
                ElementChangedFlags.BoundingBox |
                ElementChangedFlags.Transformation)) {
                var tParent = this.getParentElement();
                if (tParent != null)
                    tParent.notifyChange(ElementChangedFlags.BoundingBox);
            }
            //notify model that there was a modification
            if (this.mChangedFlags !== ElementChangedFlags.None && (tWasRemoved || this.isInScene()))
                this.mModel.notifyModification();
            this.mChangedFlags = ElementChangedFlags.None;
            this.mChangeNotifyParent = false;
        }
    };
    SceneElement.prototype.beginChange = function () {
        this.mChangeDepth++;
    };
    SceneElement.prototype.endChange = function () {
        this.mChangeDepth--;
        if (this.mChangeDepth <= 0) {
            this.mChangeDepth = 0;
            this.notifyChange(this.mChangedFlags, this.mChangeNotifyParent);
            return (true);
        }
        return (false);
    };
    SceneElement.prototype.replaceNode = function (pNewNode, pKeepTransfom, pKeepParent, pNotifyParent) {
        if (pNotifyParent === void 0) { pNotifyParent = true; }
        if (pNewNode == null)
            return;
        if (this.isDisposed) {
            Log.warn("New node assigned to already disposed SceneElement. New node will be disposed.");
            pNewNode.dispose();
            return;
        }
        if (!this.isInScene()) {
            //remove new node from scene
            removeFromScene(pNewNode, this.mModel.scene);
        }
        var tOldNode = this.mNode;
        if (tOldNode == null) {
            this.setNode(pNewNode);
            return;
        }
        var tChangeFlags = ElementChangedFlags.BoundingBox;
        this.mNode = pNewNode;
        if (pKeepParent)
            this.mNode.parent = tOldNode.parent;
        //assign NodeUserData to new node
        var tUserData = NodeUserData.FromNode(tOldNode, false);
        if (tUserData != null) {
            tUserData.assignToNode(this.mNode);
            tUserData.setData(SceneElement.NODEDATA_SCENEELEMENT, this);
        }
        //copy transformation from old to new node
        if (pKeepTransfom) {
            var tMatrix = getLocalMatrix(tOldNode);
            setLocalMatrix(this.mNode, tMatrix);
            updateFrozenWorldMatrix(this.mNode);
        }
        else
            tChangeFlags |= ElementChangedFlags.Transformation;
        //dispose old node
        tOldNode.dispose();
        this.notifyChange(tChangeFlags, pNotifyParent);
    };
    SceneElement.prototype.clone = function (pNewParent) {
        var tClone = new SceneElement(this.mModel, SceneElement.cloneNode(this.mNode, pNewParent));
        tClone.copyDataFrom(this);
        return (tClone);
    };
    SceneElement.prototype.isInScene = function () {
        if (!this.mIsInScene)
            return (false);
        //check parent
        var tParent = this.getParentElement();
        if (tParent != null)
            return (tParent.isInScene());
        return (true);
    };
    SceneElement.prototype.getNodes = function (pIncludeChildElementNodes) {
        var tNodes = new Array();
        if (this.mNode != null) {
            tNodes.push(this.mNode);
            this.collectNodes(this.mNode, tNodes, pIncludeChildElementNodes);
        }
        return (tNodes);
    };
    SceneElement.prototype.serialize = function (pContext) {
        return (null);
    };
    SceneElement.prototype.showInteractors = function (pShow) {
    };
    /**
     * Can be used to provide additional undo related data for certain operations.
     * Will be called before the operation is performed.
     */
    SceneElement.prototype.saveUndoData = function (pOperation) {
        return (null);
    };
    /**
     * Will be called for certain operations to load the undo data which was
     * saved with a previous saveUndoData() call.
     */
    SceneElement.prototype.loadUndoData = function (pOperation, pUndoData) {
    };
    ////
    //implementation of PropertyProvider
    SceneElement.prototype.getProperties = function () {
        return (Promise.resolve(null));
    };
    SceneElement.prototype.getPropertyClasses = function () {
        return (Promise.resolve(null));
    };
    SceneElement.prototype.beginPropertyChange = function () {
        this.beginChange();
    };
    SceneElement.prototype.endPropertyChange = function () {
        this.endChange();
    };
    SceneElement.FromNode = function (pNode, pCheckParents) {
        if (pNode == null)
            return (null);
        var tSceneElement = NodeUserData.GetData(pNode, SceneElement.NODEDATA_SCENEELEMENT);
        if (tSceneElement == null) {
            if (pCheckParents && pNode.parent != null)
                return (SceneElement.FromNode(pNode.parent, pCheckParents));
            else
                return (null);
        }
        return (tSceneElement);
    };
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    SceneElement.prototype._setIsSelected = function (pSelected) {
        this.mIsSelected = pSelected;
    };
    SceneElement.prototype.setNode = function (pNode) {
        this.mNode = pNode;
        if (this.mNode != null) {
            //set 'this' as user data, necessary for fromNode function
            NodeUserData.SetData(this.mNode, SceneElement.NODEDATA_SCENEELEMENT, this);
        }
        this.notifyChange(ElementChangedFlags.BoundingBox | ElementChangedFlags.Transformation);
    };
    SceneElement.prototype.copyDataFrom = function (pElement) {
    };
    SceneElement.prototype.handleImmediateChanges = function () {
        //reset cached bounding box
        if (this.wasFlagChanged(ElementChangedFlags.BoundingBox)) {
            this.mBoundingBox = null;
            this.mBoundingBoxVisible = null;
        }
        //reset cached transform
        if (this.wasFlagChanged(ElementChangedFlags.Transformation)) {
            this.mTransform = null;
            this.mWorldTransform = null;
        }
        if (this.wasFlagChanged(ElementChangedFlags.Added))
            this.mIsInScene = true;
        if (this.wasFlagChanged(ElementChangedFlags.Removed))
            this.mIsInScene = false;
    };
    SceneElement.prototype.handleDelayedChanges = function () {
    };
    SceneElement.prototype.wasAllFlagsChanged = function (pFlag) {
        return ((this.mChangedFlags & pFlag) === pFlag);
    };
    SceneElement.prototype.wasFlagChanged = function (pFlag) {
        return ((this.mChangedFlags & pFlag) != 0);
    };
    SceneElement.cloneNode = function (pNode, pNewParent, pChildNodes) {
        if (pChildNodes != null && pChildNodes) {
            // a child node may belong to a SceneElement, clone the SceneElement in this case
            var tElememt = SceneElement.FromNode(pNode, false);
            if (tElememt != null) {
                var tClonedElement = tElememt.clone(pNewParent);
                if (tClonedElement != null)
                    return (tClonedElement.node);
            }
        }
        //clone the node and its user data (keep the name)
        var tClone = pNode.clone(pNode.name, pNewParent, true);
        if (tClone == null)
            throw new Error("Failed to clone node");
        var tUserData = NodeUserData.FromNode(pNode);
        if (tUserData != null) {
            var tClonedUserData = tUserData.clone();
            tClonedUserData.assignToNode(tClone);
        }
        if (pNode.isWorldMatrixFrozen)
            tClone.freezeWorldMatrix();
        //clone the children
        var tChilds = pNode.getChildTransformNodes(true);
        for (var _i = 0, tChilds_1 = tChilds; _i < tChilds_1.length; _i++) {
            var tChild = tChilds_1[_i];
            this.cloneNode(tChild, tClone, true);
        }
        return (tClone);
    };
    SceneElement.prototype.updateCachedTransforms = function () {
        if (this.mTransform == null) {
            this.mTransform = this.mNode == null ? Matrix.Identity() : getLocalMatrix(this.mNode);
            this.mTransform.invertToRef(this.mTransformInverted);
        }
        if (this.mWorldTransform == null) {
            if (this.mNode != null) {
                this.mNode.computeWorldMatrix(true); //force update of the worldmatrix
                this.mWorldTransform = this.mNode.getWorldMatrix();
            }
            else {
                this.mWorldTransform = Matrix.Identity();
            }
            this.mWorldTransform.invertToRef(this.mWorldTransformInverted);
        }
    };
    SceneElement.prototype.collectNodes = function (pParent, pNodes, pIncludeChildElementNodes) {
        if (pParent == null)
            return;
        var tChilds = pParent.getChildTransformNodes(true);
        for (var _i = 0, tChilds_2 = tChilds; _i < tChilds_2.length; _i++) {
            var tChild = tChilds_2[_i];
            var tElement = SceneElement.FromNode(tChild, false);
            if (tElement == null || pIncludeChildElementNodes) {
                pNodes.push(tChild);
                this.collectNodes(tChild, pNodes, pIncludeChildElementNodes);
            }
        }
    };
    //// private ///////////////////////////////////////////////////////////////////////////////
    SceneElement.NODEDATA_SCENEELEMENT = "core:sceneElement";
    return SceneElement;
}());
export { SceneElement };
